import { supabase, Miembro, Diocesis, ProvinciaEclesiastica } from '../lib/supabase';
import { Usuario } from '../types/auth';

const translateAuthError = (error: string): string => {
  const translations: Record<string, string> = {
    'Invalid login credentials': 'Credenciales de inicio de sesión inválidas',
    'Email not confirmed': 'Email no confirmado',
    'User not found': 'Usuario no encontrado',
    'Invalid email or password': 'Email o contraseña inválidos',
    'Email already registered': 'El email ya está registrado',
    'Password should be at least 6 characters': 'La contraseña debe tener al menos 6 caracteres',
    'Unable to validate email address': 'No se pudo validar la dirección de email',
    'Signup requires a valid password': 'El registro requiere una contraseña válida',
    'User already registered': 'El usuario ya está registrado',
    'Password is known to be weak and easy to guess, please choose a different one.': 'La contraseña es débil y fácil de adivinar, por favor elije una contraseña fuerte.',
  };

  return translations[error] || error;
};

export const usuarioService = {
  async getAll() {
    const { data, error } = await supabase
      .from('miembros')
      .select('*')
      .not('rol_id', 'is', null)
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error('Error al obtener usuarios');
    }

    return Promise.all((data || []).map(m => this.mapMiembroToUsuario(m)));
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('miembros')
      .select('*')
      .eq('id', id)
      .not('rol_id', 'is', null)
      .single();

    if (error) {
      throw new Error('Error al obtener usuario');
    }

    return this.mapMiembroToUsuario(data);
  },

  async getByFilters(filters: {
    rol_id?: string;
    lumisial_id?: string;
    diocesis_id?: string;
    activo?: boolean;
  }) {
    // @supabase/supabase-js 2.110 profundizó los genéricos del builder; sin este
    // tipo el reencadenado condicional dispara TS2589 (instanciación infinita).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let query: any = supabase
      .from('miembros')
      .select('*')
      .not('rol_id', 'is', null);

    if (filters.rol_id) {
      query = query.eq('rol_id', filters.rol_id);
    }

    if (filters.lumisial_id) {
      query = query.eq('lumisial_actual_id', filters.lumisial_id);
    }

    if (filters.activo !== undefined) {
      if (filters.activo) {
        query = query.not('rol_id', 'is', null);
      } else {
        query = query.is('rol_id', null);
      }
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) {
      throw new Error('Error al filtrar usuarios');
    }

    return Promise.all((data || []).map((m: Miembro) => this.mapMiembroToUsuario(m)));
  },

  async getStats() {
    const { data, error } = await supabase
      .from('miembros')
      .select('id, rol_id, rol:roles(nombre)')
      .not('rol_id', 'is', null);

    if (error) {
      throw new Error('Error al obtener estadísticas');
    }

    const total = data?.length || 0;
    const activos = data?.filter((u) => u.rol_id !== null).length || 0;
    const porRol = ((data || []) as unknown as Array<{ rol_id: string | null; rol?: { nombre: string } | null }>).reduce((acc: Record<string, number>, user) => {
      const rolNombre = user.rol?.nombre || 'Sin rol';
      acc[rolNombre] = (acc[rolNombre] || 0) + 1;
      return acc;
    }, {});

    return {
      total,
      activos,
      inactivos: total - activos,
      porRol,
    };
  },

  async getTotalActivos(): Promise<number> {
    const { count, error } = await supabase
      .from('miembros')
      .select('id', { count: 'exact', head: true })
      .not('rol_id', 'is', null);

    if (error) {
      throw new Error('Error al obtener total de usuarios activos');
    }

    return count || 0;
  },

  async getUserAuthInfo(userId: string): Promise<{ hasLoggedIn: boolean; lastSignIn: string | null; hasCredentials: boolean }> {
    const { data: miembro, error: miembroError } = await supabase
      .from('miembros')
      .select('user_id')
      .eq('id', userId)
      .single();

    if (miembroError || !miembro) {
      throw new Error('Usuario no encontrado');
    }

    // Si user_id es null, el usuario nunca ha tenido credenciales asignadas
    if (!miembro.user_id) {
      return {
        hasLoggedIn: false,
        lastSignIn: null,
        hasCredentials: false
      };
    }

    // Si tiene user_id, asumimos que tiene credenciales asignadas
    // En el futuro, podemos consultar auth.users via edge function para obtener last_sign_in_at
    return {
      hasLoggedIn: false, // Por ahora asumimos false, puede mejorarse
      lastSignIn: null,
      hasCredentials: true
    };
  },

  async updatePassword(userId: string, newPassword: string) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No hay sesión activa');
    }

    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-user-password`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        newPassword,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success) {
      throw new Error(translateAuthError(result.error || 'Error al actualizar contraseña'));
    }

    return result.data;
  },

  async mapMiembroToUsuario(miembro: Miembro): Promise<Usuario> {
    const { data: rolData } = await supabase
      .from('roles')
      .select('*')
      .eq('id', miembro.rol_id)
      .single();

    const { data: lumisialData } = await supabase
      .from('lumisiales')
      .select('*')
      .eq('id', miembro.lumisial_actual_id)
      .single();

    let diocesisData: Diocesis | null = null;
    let provinciaData: ProvinciaEclesiastica | null = null;

    if (lumisialData?.diocesis_id) {
      const { data: diocesis } = await supabase
        .from('diocesis')
        .select('*')
        .eq('id', lumisialData.diocesis_id)
        .single();
      diocesisData = diocesis;

      if (diocesis?.provincia_eclesiastica_id) {
        const { data: provincia } = await supabase
          .from('provincias_eclesiasticas')
          .select('*')
          .eq('id', diocesis.provincia_eclesiastica_id)
          .single();
        provinciaData = provincia;
      }
    }

    if (lumisialData?.provincia_eclesiastica_id && !provinciaData) {
      const { data: provincia } = await supabase
        .from('provincias_eclesiasticas')
        .select('*')
        .eq('id', lumisialData.provincia_eclesiastica_id)
        .single();
      provinciaData = provincia;
    }

    return {
      id: miembro.id,
      user_id: miembro.user_id ?? '',
      rol_id: miembro.rol_id ?? '',
      provincia_eclesiastica_id: provinciaData?.id || lumisialData?.provincia_eclesiastica_id || null,
      lumisial_id: miembro.lumisial_actual_id,
      diocesis_id: diocesisData?.id || lumisialData?.diocesis_id || null,
      nombres: miembro.nombres,
      apellidos: miembro.apellidos,
      email: miembro.email ?? '',
      telefono: miembro.telefono ?? null,
      activo: miembro.rol_id !== null,
      created_at: miembro.created_at,
      updated_at: miembro.updated_at,
      foto_url: miembro.foto_url ?? null,
      rol: rolData,
      lumisial: lumisialData
        ? { ...lumisialData, diocesis: diocesisData ?? undefined, provincia_eclesiastica: provinciaData ?? undefined }
        : null,
      diocesis: diocesisData
    };
  }
};
